import type { GetApiV1ThreeLetterCodeClientAdminsClient200 } from '@fukuri-admins/apis/client/gen/model';
import { formatDateString } from '@fukuri-admins/utils/common/date';
import {
  CARD_BRAND_LABELS,
  ERROR_MESSAGE,
  FAILED_PAYMENT_NOTICE,
  SETTLEMENT_METHOD_LABELS,
  STRIPE_SOURCE_PARAM,
} from '../../constants';
import type { useGetStripeCreditCard } from '../../hooks/useGetStripeCreditCard';
import type { ContractInfoPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ContractInfoPresentation>;

type Args = {
  /** 導入企業情報取得 API のレスポンスデータ */
  data: GetApiV1ThreeLetterCodeClientAdminsClient200;
  /**
   * 既定カード情報の取得結果（登録済み / 未登録 / 取得失敗）
   *
   * hook 側の型は export できないため（API hook からの型 export は禁止）、
   * Component Hook と同じく戻り値型から引く
   */
  creditCardResult: ReturnType<typeof useGetStripeCreditCard>['data'];
  /**
   * URL の `stripe_source` クエリの値。付いていない場合は null。
   *
   * 決済失敗の案内メール（dunning）から遷移してきたかの判定に使う
   */
  stripeSource: string | null;
};

// この converter が担うのは API レスポンス由来の表示値のみ。フォームの開閉状態等の
// Props は Component Hook が持つため、Pick で責務範囲を型として明示する
type IConvertToPresentationProps = (
  args: Args
) => Pick<
  PresentationProps,
  | 'companyInfo'
  | 'planInfo'
  | 'contactInfo'
  | 'billingInfo'
  | 'settlementMethodLabel'
  | 'isCardPayment'
  | 'cardLabel'
  | 'cardFetchErrorMessage'
  | 'failedPaymentNoticeMessage'
>;

/**
 * 導入企業情報と Stripe 既定カード情報を ContractInfoPresentation の Props に変換する
 *
 * `private_client` の required は `id` / `name` / `giftee_plan` / `gift_plan` のみで
 * 他は全て nullable なため、未設定値は表示用の '-' に正規化する
 * （既存実装の慣例に合わせ `||` フォールバックを使用する）。
 * カードブランドは未知の値の場合、元の値をそのまま表示する。
 *
 * カード情報の表示可否は `settlement_method` から導出した `isCardPayment` に集約する。
 * カード払い以外（請求書払い / 契約なし）では Stripe Customer が結線されておらず、
 * 既定カード情報 API も SetupIntent 発行 API も 404 を返すため、cardLabel は
 * 取得結果に関わらず undefined を返す（Presentation 側はカード情報行と
 * カード変更フォームを描画しない）。カード情報の取得失敗（404 以外）の案内と、
 * 決済失敗メールからの遷移の案内も同様に、カード払いのご契約のときだけ出す。
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data: { client },
  creditCardResult,
  stripeSource,
}) => ({
  companyInfo: {
    corporateNumber: client.corporate_number || '-',
    companyName: client.name,
    companyNameKana: client.name_kana || '-',
    representativeName: client.representative_name || '-',
    address: {
      postalCode: client.postal_code
        ? `${client.postal_code.slice(0, 3)}-${client.postal_code.slice(3)}`
        : '-',
      prefectureCity:
        client.prefecture || client.city
          ? `${client.prefecture?.name ?? ''}${client.city ?? ''}`
          : '-',
      streetAddress: client.street_address || '-',
    },
    partnerBranchName: client.partner_branch?.name || '-',
  },
  planInfo: {
    contractPlanName: client.contract_plan?.name || '-',
    contractStartedOnLabel: client.contract_started_on
      ? formatDateString({ dateString: client.contract_started_on, formatType: 'yyyy/MM/dd' })
      : '-',
  },
  contactInfo: {
    contactPersonName: client.contact_person_name || '-',
    contactPersonNameKana: client.contact_person_name_kana || '-',
    contactPersonEmail: client.contact_person_email || '-',
    contactPersonPhone: client.contact_person_phone || '-',
  },
  billingInfo: {
    billingContactName: client.billing_contact_name || '-',
    billingContactNameKana: client.billing_contact_name_kana || '-',
    billingEmail: client.billing_email || '-',
  },
  settlementMethodLabel: client.settlement_method
    ? SETTLEMENT_METHOD_LABELS[client.settlement_method]
    : '-',
  isCardPayment: client.settlement_method === 'card',
  cardLabel:
    client.settlement_method === 'card' && creditCardResult.status === 'registered'
      ? `${CARD_BRAND_LABELS[creditCardResult.creditCard.brand] ?? creditCardResult.creditCard.brand}（‥‥${creditCardResult.creditCard.last4}）`
      : undefined,
  cardFetchErrorMessage:
    client.settlement_method === 'card' && creditCardResult.status === 'failed'
      ? ERROR_MESSAGE.cardFetchFailed
      : undefined,
  failedPaymentNoticeMessage:
    client.settlement_method === 'card' && stripeSource === STRIPE_SOURCE_PARAM.failedPayment
      ? FAILED_PAYMENT_NOTICE
      : undefined,
});
